import {createContext, useCallback, useContext, useEffect, useState} from "react";

import {DrawTools, drawState} from "./DrawState";

const DrawV2Context = createContext();

export function useDrawV2() {
  return useContext(DrawV2Context);
}

export const DrawV2Consumer = DrawV2Context.Consumer;

export function DrawV2Provider({children}) {
  const [isDrawing, setIsDrawing] = useState(false);
  const [polygon, setPolygon] = useState(false);
  const [circle, setCircle] = useState(false);
  const [file, setFile] = useState(false);
  const [point, setPoint] = useState(false);
  const [currentMode, setCurrentMode] = useState(null);

  const syncDrawState = useCallback(() => {
    setIsDrawing(drawState.isDrawing());
    setPolygon(drawState.acceptPolygon());
    setCircle(drawState.acceptCircle());
    setFile(drawState.acceptFile());
    setPoint(drawState.acceptPoint());
  }, []);

  const deleteAllLayers = useCallback(() => {
    drawState.deleteAll();
  }, []);

  const startDrawing = useCallback(({tools, onCreate, onUpdate, onDelete, keepModeAfterCreate}) => {
    drawState.start({tools, onCreate, onUpdate, onDelete, keepModeAfterCreate});
    syncDrawState();
    setCurrentMode(null);
  }, [syncDrawState]);

  const endDrawing = useCallback(() => {
    drawState.end();
    syncDrawState();
    setCurrentMode(null);
    deleteAllLayers();
  }, [deleteAllLayers, syncDrawState]);

  const mode = useCallback((nextMode) => {
    drawState.mode(nextMode);
    setCurrentMode(nextMode);
  }, []);

  const cancelMode = useCallback(() => {
    drawState.cancelMode();
    syncDrawState();
    setCurrentMode(null);
  }, [syncDrawState]);

  useEffect(() => {
    if (currentMode == null) {
      return undefined;
    }

    const onEscape = (event) => {
      if (event.key !== "Escape") {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation?.();
      cancelMode();
    };

    window.addEventListener("keydown", onEscape, true);
    window.addEventListener("keyup", onEscape, true);

    return () => {
      window.removeEventListener("keydown", onEscape, true);
      window.removeEventListener("keyup", onEscape, true);
    };
  }, [cancelMode, currentMode]);

  const deleteLayer = useCallback((layerId) => {
    drawState.deleteLayer(layerId);
  }, []);

  const getAll = useCallback(() => {
    return drawState.getAll();
  }, []);

  const add = useCallback((nextPolygon) => {
    return drawState.add(nextPolygon);
  }, []);

  const get = useCallback((featureId) => {
    return drawState.get(featureId);
  }, []);

  const directSelect = useCallback((featureId, mode = DrawTools.POLYGON) => {
    drawState.directSelect(featureId);
    syncDrawState();
    setCurrentMode(mode);
  }, [syncDrawState]);

  const forceCallback = useCallback((arg1, arg2) => {
    drawState.forceCallback(arg1, arg2);
  }, []);

  const value = {
    startDrawing,
    endDrawing,
    add,
    get,
    mode,
    cancelMode,
    deleteLayer,
    deleteAllLayers,
    getAll,
    directSelect,
    forceCallback,
    isDrawing,
    polygon,
    circle,
    file,
    point,
    currentMode,
  };

  return <DrawV2Context.Provider value={value}>{children}</DrawV2Context.Provider>;
}
